import {
  mapTypographyScale,
  type TypographyMetrics,
  type TypographyVariantPath,
  type TextTransform,
} from "./typography-scale";

export type {
  TypographyCategory,
  TypographyVariant,
  TypographyVariantPath,
} from "./typography-scale";

export const fontStacks = {
  inter: `"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`,
  archivo: `"Archivo", "Inter", -apple-system, BlinkMacSystemFont, sans-serif`,
} as const;

export type FontStack = keyof typeof fontStacks;

export interface TypographyStyle {
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  lineHeight: number;
  letterSpacing: number;
  textTransform?: TextTransform;
}

const toCssStyle = (metrics: TypographyMetrics): TypographyStyle => {
  const style: TypographyStyle = {
    fontFamily: fontStacks[metrics.typeface],
    fontSize: metrics.fontSize,
    fontWeight: metrics.fontWeight,
    lineHeight: metrics.lineHeight,
    letterSpacing: metrics.letterSpacing,
  };
  if (metrics.textTransform) style.textTransform = metrics.textTransform;
  return style;
};

export const typography = mapTypographyScale(toCssStyle);

export type Typography = typeof typography;

const variantGroups: Record<string, Record<string, TypographyStyle>> = {
  heading: typography.heading,
  body: typography.body,
  label: typography.label,
  caption: typography.caption,
  number: typography.number,
};

/** Resolve a variant path to its style object. */
export const typographyStyle = (
  path: TypographyVariantPath
): TypographyStyle => {
  if (path === "display") {
    return typography.display;
  }
  const separator = path.indexOf(".");
  return variantGroups[path.slice(0, separator)][path.slice(separator + 1)];
};

/**
 * Render a variant as a CSS declaration block, safe to interpolate inside a
 * styled-components template literal.
 */
export const typographyCSS = (path: TypographyVariantPath): string => {
  const style = typographyStyle(path);
  const declarations = [
    `font-family: ${style.fontFamily};`,
    `font-size: ${style.fontSize}px;`,
    `font-weight: ${style.fontWeight};`,
    `line-height: ${style.lineHeight}px;`,
    `letter-spacing: ${style.letterSpacing}px;`,
  ];
  if (style.textTransform) {
    declarations.push(`text-transform: ${style.textTransform};`);
  }
  return declarations.join("\n");
};
